/**
 * Adds a device to an existing check-in session.
 *
 * Reception needs this because a check-in does not always start with the
 * device list: "Start on wall" creates the session with no devices at all, so
 * the customer can scan the TV and enter their own details while staff are
 * still holding the machine. Without a way in here, that session had nothing
 * to inspect and no way to add anything — the Devices card said "No devices on
 * this check-in yet" and stopped there.
 *
 * Hosts the same composer as the create page (`DraftJobAccordionRow`), so a
 * device added later gets the same device-type / service / charge treatment —
 * including the auto-seeded diagnostic line — as one added up front.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import type {
  CheckInChargeLine,
  CheckInDraftJob,
  CheckInDraftJobCreate,
  CheckInIntakeType,
  CheckInTicketGrouping,
} from '@techgitter/contracts';

import { addCheckInDraftJob } from '../../../../services/checkInApi';
import {
  DraftJobAccordionRow,
  createDraftJobFormState,
  type DraftJobFormState,
} from '../../DraftJobAccordionRow';
import './checkInDialog.css';

export interface AddDeviceDialogProps {
  open: boolean;
  sessionId: string;
  /** Index of the device being added, for the row's "Device N" label. */
  deviceIndex: number;
  intakeType?: CheckInIntakeType | null;
  originalWorkItemId?: string | null;
  /**
   * The session's ticket grouping. In `single_job` the service summary belongs
   * to the job rather than the device, so devices after the first inherit the
   * first one's and do not render the field — the server copies it across.
   */
  ticketGrouping?: CheckInTicketGrouping | null;
  /** Core / Core+ customers are billed monthly, so no diagnostic fee is seeded. */
  isCoreCustomer?: boolean;
  onClose: () => void;
  onAdded: (job: CheckInDraftJob) => void;
}

export const AddDeviceDialog: React.FC<AddDeviceDialogProps> = ({
  open,
  sessionId,
  deviceIndex,
  intakeType = null,
  originalWorkItemId = null,
  ticketGrouping = null,
  isCoreCustomer = false,
  onClose,
  onAdded,
}) => {
  const isExpressSession = intakeType === 'express';
  // `deviceIndex` is the 0-based index of the device being added, so anything
  // past the first has a device 1 to inherit from.
  const inheritsJobFields = ticketGrouping === 'single_job' && deviceIndex > 0;
  const [job, setJob] = useState<DraftJobFormState>(createDraftJobFormState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fresh row each time the sheet opens — an abandoned half-filled device
  // must not reappear on the next customer.
  useEffect(() => {
    if (!open) return;
    setJob(createDraftJobFormState());
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const update = (patch: Partial<DraftJobFormState>) => setJob(prev => ({ ...prev, ...patch }));
  const setCharges = (charges: CheckInChargeLine[]) =>
    setJob(prev => ({ ...prev, preliminaryCharges: charges }));

  const handleSubmit = async () => {
    const serviceSummary = job.serviceSummary.trim();
    if (job.deviceType === 'other' && !job.deviceTypeOther.trim()) {
      setError('Say what the device is.');
      return;
    }
    if (!serviceSummary && !inheritsJobFields) {
      setError('Service Summary is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: CheckInDraftJobCreate = {
        deviceType: job.deviceType,
        ...(job.deviceType === 'other' ? { deviceTypeOther: job.deviceTypeOther.trim() } : {}),
        // Summary and service mode are omitted when inherited, so the server
        // copies device 1's rather than this dialog sending its own form
        // defaults — `serviceMode` in particular starts at
        // 'diagnose_and_quote', which would silently overwrite a device 1 set
        // to 'prepaid_in_full'.
        ...(inheritsJobFields ? {} : { serviceMode: job.serviceMode, serviceSummary }),
        requirements: job.requirements.trim() || undefined,
        customerVisibleDetails: job.customerVisibleDetails.trim() || undefined,
        internalNotes: job.internalNotes.trim() || undefined,
        // Express collects no money at check-in — same rule the create page
        // applies, so a charge composed before the type was known is dropped.
        // An inheriting device carries none either: the job is diagnosed once,
        // so a second diagnostic fee would be charging twice for one job.
        preliminaryCharges:
          isExpressSession || inheritsJobFields ? [] : job.preliminaryCharges,
      };
      const created = await addCheckInDraftJob(sessionId, payload);
      onAdded(created);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add device');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog ci-dialog--wide" role="dialog" aria-modal="true" aria-label="Add device">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Add device</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body ci-dialog__body--scroll">
          <DraftJobAccordionRow
            index={deviceIndex}
            job={job}
            expanded
            showRemove={false}
            isExpress={isExpressSession}
            intakeType={intakeType}
            originalWorkItemId={originalWorkItemId}
            isCoreCustomer={isCoreCustomer}
            inheritsJobFields={inheritsJobFields}
            onToggle={() => undefined}
            onChange={update}
            onChargesChange={setCharges}
            onRemove={() => undefined}
          />

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button type="button" className="ci-dialog__cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Adding…' : 'Add device'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
